/**
 * 叫貨與出貨(後台)共用的小工具:日期顯示、瓶數文字、後端錯誤碼 → 中文訊息
 *
 * API 回來的日期已經是台北時間的 ISO 字串(2026-10-24T10:00:00+08:00),
 * 這裡一律用 slice 取日期 / 時間,不經過 Date 的時區轉換;今天用 utils/date.ts 的 todayTpe()。
 */
import type { SupplyError, SupplyQty, SupplyShipError } from "../api/admin";


export const CAT_NAME: Record<string, string> = {
  drink: "即飲",
  fresh: "鮮燉",
};

const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"];

function parseYmd(iso: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

/** "週四";不是日期回 "" */
export function weekdayLabel(iso: string): string {
  const p = parseYmd(iso);
  if (!p) return "";
  return `週${WEEKDAYS[new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay()]}`;
}

/** "11/5"(pad = true 補零成 "11/05");不是日期原樣回傳 */
export function fmtMD(iso: string, pad = false): string {
  const p = parseYmd(iso);
  if (!p) return iso || "—";
  const mm = pad ? String(p.m).padStart(2, "0") : String(p.m);
  const dd = pad ? String(p.d).padStart(2, "0") : String(p.d);
  return `${mm}/${dd}`;
}

/** "11/05(週四)" */
export function fmtMDWeek(iso: string): string {
  if (!parseYmd(iso)) return iso || "—";
  return `${fmtMD(iso, true)}(${weekdayLabel(iso)})`;
}

/** 兩個 YYYY-MM-DD 相差幾天(to - from) */
export function daysBetween(fromIso: string, toIso: string): number {
  const a = parseYmd(fromIso);
  const b = parseYmd(toIso);
  if (!a || !b) return 0;
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000);
}

/** "11/5 10:00" */
export function fmtMDHM(iso: string | null | undefined): string {
  if (!iso) return "—";
  return `${fmtMD(iso)} ${String(iso).slice(11, 16)}`;
}

/** 克數:最多一位小數,千分位 */
export function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return n.toLocaleString("zh-TW", { maximumFractionDigits: 1 });
}

/** "即飲 2 / 鮮燉 1" */
export function qtyText(q: SupplyQty | null | undefined): string {
  if (!q) return "—";
  return `即飲 ${q.drink} / 鮮燉 ${q.fresh}`;
}

/** 輸入框的字串 → 0 以上的整數;不合法回 null */
export function parseQty(text: string): number | null {
  const t = String(text ?? "").trim();
  if (t === "") return 0;
  if (!/^\d+$/.test(t)) return null;
  return Number(t);
}

/** 後端 400 / 404 的 status 碼 → 短的中文訊息(toast 用) */
export function supplyErrorText(r: SupplyError): string {
  const cat = r.category ? CAT_NAME[r.category] || r.category : "";
  switch (r.status) {
    case "invalid_qty":
      return cat ? `${cat}瓶數不正確(破損不可超過實收)` : "瓶數不正確";
    case "invalid_order_days":
      return "下單日請填 1 到 31 日,最多 6 個";
    case "invalid_lead_working_days":
      return "交期工作天請填 0 到 60";
    case "invalid_arrival_working_days":
      return "出貨到到貨工作天請填 0 到 10";
    case "invalid_ship_working_days":
      return "到貨到出貨工作天請填 0 到 10";
    case "invalid_grams_per_bottle":
      return cat ? `${cat}每瓶克數請填 0 到 1000` : "每瓶克數請填 0 到 1000";
    case "invalid_buffer_bottles":
      return cat ? `${cat}緩衝瓶數不正確` : "緩衝瓶數不正確";
    case "invalid_extra":
      return cat ? `${cat}其他需求瓶數不正確` : "其他需求瓶數不正確";
    case "invalid_stock_offset":
      return cat ? `${cat}現有成品瓶數不正確` : "現有成品瓶數不正確";
    case "invalid_holidays":
      return "不出貨日期格式不正確";
    case "invalid_carrier":
      return "物流方式不正確";
    case "invalid_start_date":
      return "首次出貨日期格式不正確";
    case "invalid_date":
      return "日期格式不正確";
    case "invalid_ship_date":
      return "出貨日期格式不正確";
    case "invalid_grams":
      return "克數不正確";
    case "invalid_type":
      return "登記類型不正確";
    case "invalid_lines":
      return "名單格式不正確";
    case "invalid_member_uids":
      return "出貨會員清單格式不正確";
    case "member_not_found":
      return "找不到這位會員";
    case "member_merged":
      return "這位會員已合併到其他帳號,不能設定";
    case "empty_order":
      return "下單瓶數不可全部為 0";
    case "empty_arrival":
      return "到貨瓶數不可全部為 0";
    case "not_ordered":
      return "這一批還沒確認下單";
    case "has_arrivals_or_shipments":
      return "已有到貨或出貨紀錄,不能取消下單";
    case "no_admin_group":
      return "還沒綁定管理員 LINE 群組,無法推播";
    case "no_changes":
      return "沒有可儲存的變更";
    case "no_claim":
      return "這位會員目前沒有待查核的出貨";
    case "claim_in_progress":
      return "這位會員的出貨或查核還在處理中,請 10 分鐘後再查核";
    case "claim_changed":
      return "查核期間狀態有變動,請重新整理後再看一次";
    case "refund_failed":
      return "退回存摺沒有成功,請 10 分鐘後再按一次查核";
    case "claim_unverifiable":
      return "這筆出貨沒有編號,無法自動核對,請聯繫橙築處理";
    case "not_found":
      return "找不到資料";
    default:
      return `操作未成功:${r.status}`;
  }
}

/** 批次出貨時單一會員失敗的原因 */
export function shipFailText(e: SupplyShipError | null | undefined): string {
  if (!e) return "";
  if (e.status === "insufficient_balance") {
    const cat = e.category ? CAT_NAME[e.category] || e.category : "";
    return `存摺不足(${cat} 餘 ${e.balance ?? 0}、需 ${e.needed ?? 0})`;
  }
  if (e.status === "member_not_found") return "會員不存在";
  if (e.status === "unknown") return "狀態不明,可能已扣存摺,請查核";
  if (e.status === "fenced") return "這筆已由查核接手處理,請重新整理";
  return e.status;
}
